import { z } from "zod";
import { ACTIVITY_STATUSES, PARTICIPANT_TYPES, VALIDATION_STATUSES } from "../enums.js";
import { optionalText, optionalUrl, timeString } from "./common.js";
/** Teks yang boleh dikosongkan saat pembaruan ("" atau null berarti hapus isi). */
const clearableText = (max) => z.string().trim().max(max).nullish();
const clearableTime = () => timeString.or(z.literal("")).nullish();
const baseActivityFields = {
    title: z.string().trim().min(3, "Judul kegiatan minimal 3 karakter.").max(255),
    description: z.string().trim().min(10, "Deskripsi minimal 10 karakter.").max(5000),
    activityDate: z.coerce.date({ message: "Tanggal pelaksanaan tidak valid." }),
    startTime: timeString.optional(),
    endTime: timeString.optional(),
    location: z.string().trim().min(2, "Lokasi wajib diisi.").max(255),
    categoryId: z.string().min(1, "Kategori wajib dipilih."),
    academicYearId: z.string().min(1, "Tahun akademik wajib dipilih."),
    semesterId: z.string().min(1, "Semester wajib dipilih."),
    organizer: z.string().trim().min(2, "Penyelenggara wajib diisi.").max(255),
    responsiblePerson: z.string().trim().min(2, "Penanggung jawab wajib diisi.").max(255),
    output: optionalText(2000),
    outcome: optionalText(2000),
    externalLink: optionalUrl,
};
/**
 * Skema pembuatan kegiatan.
 *
 * PENTING: kepemilikan (institutionId, facultyId, submittedById, status,
 * validationStatus) TIDAK ADA di sini — nilainya selalu ditetapkan server dari
 * sesi pengguna, sehingga tidak bisa diisi dari body (anti mass-assignment).
 * studyProgramId/unitId hanya dihormati untuk role admin dan tetap diverifikasi
 * terhadap lingkup wewenangnya.
 */
export const activityCreateSchema = z.object({
    ...baseActivityFields,
    studyProgramId: optionalText(64),
    unitId: optionalText(64),
});
/** Skema pembaruan: seluruh field opsional; kepemilikan tidak dapat diubah. */
export const activityUpdateSchema = z.object({
    title: baseActivityFields.title.optional(),
    description: baseActivityFields.description.optional(),
    activityDate: baseActivityFields.activityDate.optional(),
    startTime: clearableTime(),
    endTime: clearableTime(),
    location: baseActivityFields.location.optional(),
    categoryId: baseActivityFields.categoryId.optional(),
    academicYearId: baseActivityFields.academicYearId.optional(),
    semesterId: baseActivityFields.semesterId.optional(),
    organizer: baseActivityFields.organizer.optional(),
    responsiblePerson: baseActivityFields.responsiblePerson.optional(),
    output: clearableText(2000),
    outcome: clearableText(2000),
    externalLink: clearableText(500),
});
export const activityListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(20),
    categoryId: z.string().optional(),
    academicYearId: z.string().optional(),
    semesterId: z.string().optional(),
    studyProgramId: z.string().optional(),
    status: z.enum(ACTIVITY_STATUSES).optional(),
    validationStatus: z.enum(VALIDATION_STATUSES).optional(),
    q: z.string().trim().max(200).optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    sort: z.enum(["activityDate", "createdAt", "title"]).default("activityDate"),
    order: z.enum(["asc", "desc"]).default("desc"),
});
export const participantCreateSchema = z.object({
    name: z.string().trim().min(2, "Nama peserta wajib diisi.").max(255),
    roleInActivity: z.string().trim().min(2, "Peran dalam kegiatan wajib diisi.").max(100),
    participantType: z.enum(PARTICIPANT_TYPES),
    userId: optionalText(64),
});
export const evidenceMetaSchema = z.object({
    evidenceType: z.string().trim().min(2, "Jenis bukti wajib dipilih.").max(50),
    description: optionalText(1000),
});
export const reportQuerySchema = z.object({
    format: z.enum(["json", "csv"]).default("json"),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    categoryId: z.string().optional(),
    academicYearId: z.string().optional(),
    semesterId: z.string().optional(),
    validationStatus: z.enum(VALIDATION_STATUSES).optional(),
    studyProgramId: z.string().optional(),
});
//# sourceMappingURL=activity.js.map